import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Plus, Edit2, Trash2, Search, Tags, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, GitMerge, Users
} from 'lucide-react';
import { tagsService, type ConversationTag } from '@/lib/api/services/tags.service';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const PAGE_SIZE = 15;

// Stat card component
function StatCard({
  icon: Icon,
  count,
  label,
  iconBgClass = 'bg-[rgba(70,105,237,0.10)]'
}: {
  icon: React.ElementType;
  count: number;
  label: string;
  iconBgClass?: string;
}) {
  return (
    <div className="bg-white rounded-[40px] border border-slate-200 flex-1 h-[214px] flex flex-col items-center justify-center">
      <div className={cn("w-16 h-16 rounded-full flex items-center justify-center mb-4", iconBgClass)}>
        <Icon className="w-6 h-6 text-[#4669ed]" />
      </div>
      <div className="text-[40px] leading-[42px] font-normal text-slate-900 tracking-tight">
        {count}
      </div>
      <div className="text-sm text-slate-500 mt-2">
        {label}
      </div>
    </div>
  );
}

// Status badge component
function ActiveBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[rgba(16,185,129,0.10)] text-emerald-500">
        <CheckCircle2 className="w-3 h-3" />
        Active
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
      <XCircle className="w-3 h-3" />
      Inactive
    </span>
  );
}

export default function TagsManagement() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ConversationTag | null>(null);
  const [deleteConfirmTag, setDeleteConfirmTag] = useState<ConversationTag | null>(null);
  const [mergeSourceTag, setMergeSourceTag] = useState<ConversationTag | null>(null);
  const [mergeTargetTagId, setMergeTargetTagId] = useState<string>('');

  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);

  // Fetch tags
  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags', activeFilter === 'active'],
    queryFn: () => tagsService.getTags(activeFilter === 'active'),
  });

  // Create tag mutation
  const createTag = useMutation({
    mutationFn: (data: { name: string; description?: string }) => tagsService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setIsAddDialogOpen(false);
      setNewTagName('');
      setNewTagDescription('');
      toast.success('Tag created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create tag');
    },
  });

  // Update tag mutation
  const updateTag = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; description?: string } }) =>
      tagsService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setEditingTag(null);
      toast.success('Tag updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update tag');
    },
  });

  // Delete tag mutation
  const deleteTag = useMutation({
    mutationFn: (id: string) => tagsService.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setDeleteConfirmTag(null);
      toast.success('Tag deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete tag');
    },
  });

  // Merge tag mutation
  const mergeTag = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      tagsService.mergeTag(sourceId, targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setMergeSourceTag(null);
      setMergeTargetTagId('');
      toast.success('Tag merged successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to merge tag');
    },
  });

  const filteredTags = useMemo(() => {
    return tags.filter(tag => {
      const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tag.description && tag.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesActive = activeFilter === 'all' ||
        (activeFilter === 'active' && tag.is_active) ||
        (activeFilter === 'inactive' && !tag.is_active);
      return matchesSearch && matchesActive;
    });
  }, [tags, searchQuery, activeFilter]);

  // Pagination
  const totalCount = filteredTags.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedTags = filteredTags.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Stats
  const stats = useMemo(() => {
    return {
      total: tags.length,
      active: tags.filter(t => t.is_active).length,
      inactive: tags.filter(t => !t.is_active).length,
      totalAssignments: tags.reduce((sum, t) => sum + (t.assignment_count || 0), 0),
    };
  }, [tags]);

  const handleAddTag = async () => {
    if (!newTagName.trim()) return;

    await createTag.mutateAsync({
      name: newTagName.trim(),
      description: newTagDescription.trim() || undefined,
    });
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !newTagName.trim()) return;

    await updateTag.mutateAsync({
      id: editingTag.id,
      data: {
        name: newTagName.trim(),
        description: newTagDescription.trim() || undefined,
      },
    });
  };

  const handleDeleteTag = async () => {
    if (!deleteConfirmTag) return;
    await deleteTag.mutateAsync(deleteConfirmTag.id);
  };

  const handleMergeTag = async () => {
    if (!mergeSourceTag || !mergeTargetTagId) return;
    await mergeTag.mutateAsync({
      sourceId: mergeSourceTag.id,
      targetId: mergeTargetTagId,
    });
  };

  const openEditDialog = (tag: ConversationTag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setNewTagDescription(tag.description || '');
  };

  const openMergeDialog = (tag: ConversationTag) => {
    setMergeSourceTag(tag);
    setMergeTargetTagId('');
  };

  const activeTagsForMerge = tags.filter(t => t.is_active && t.id !== mergeSourceTag?.id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading tags...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="flex gap-4">
        <StatCard icon={Tags} count={stats.total} label="Total Tags" />
        <StatCard icon={CheckCircle2} count={stats.active} label="Active Tags" iconBgClass="bg-[rgba(16,185,129,0.10)]" />
        <StatCard icon={XCircle} count={stats.inactive} label="Inactive Tags" iconBgClass="bg-slate-100" />
        <StatCard icon={Users} count={stats.totalAssignments} label="Total Assignments" iconBgClass="bg-[rgba(139,92,246,0.10)]" />
      </div>

      {/* Actions and Filters */}
      <div className="bg-white rounded-[40px] border border-slate-200 p-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Select value={activeFilter} onValueChange={(v) => {
              setActiveFilter(v as 'all' | 'active' | 'inactive');
              setPage(1);
            }}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setIsAddDialogOpen(true)} className="bg-[#4669ed] hover:bg-[#3d5dd8]">
              <Plus className="w-4 h-4 mr-2" />
              Add Tag
            </Button>
          </div>
        </div>

        {/* Tags Table */}
        <div className="border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assignments</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedTags.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No tags found
                  </td>
                </tr>
              ) : (
                paginatedTags.map((tag) => (
                  <tr key={tag.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{tag.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-600">{tag.description || '-'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-600">{tag.assignment_count || 0}</div>
                    </td>
                    <td className="px-4 py-3">
                      <ActiveBadge isActive={tag.is_active} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(tag)}
                          disabled={!tag.is_active}
                          title="Edit Tag"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openMergeDialog(tag)}
                          disabled={!tag.is_active || tags.filter(t => t.is_active && t.id !== tag.id).length === 0}
                          title="Merge Tag"
                        >
                          <GitMerge className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmTag(tag)}
                          title="Delete Tag"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-slate-600">
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} tags
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen || editingTag !== null} onOpenChange={(open) => {
        if (!open) {
          setIsAddDialogOpen(false);
          setEditingTag(null);
          setNewTagName('');
          setNewTagDescription('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Create New Tag'}</DialogTitle>
            <DialogDescription>
              {editingTag ? 'Update the tag name and description.' : 'Create a new tag for categorizing conversations.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tag-name">Name *</Label>
              <Input
                id="tag-name"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g., pricing_discussion"
              />
            </div>
            <div>
              <Label htmlFor="tag-description">Description</Label>
              <Input
                id="tag-description"
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddDialogOpen(false);
              setEditingTag(null);
              setNewTagName('');
              setNewTagDescription('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={editingTag ? handleUpdateTag : handleAddTag}
              disabled={!newTagName.trim() || createTag.isPending || updateTag.isPending}
            >
              {editingTag ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmTag !== null} onOpenChange={(open) => {
        if (!open) setDeleteConfirmTag(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Tag</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirmTag?.name}"? This will mark the tag as inactive.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmTag(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTag} disabled={deleteTag.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={mergeSourceTag !== null} onOpenChange={(open) => {
        if (!open) {
          setMergeSourceTag(null);
          setMergeTargetTagId('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Tag</DialogTitle>
            <DialogDescription>
              Merge "{mergeSourceTag?.name}" into another tag. All assignments will be moved to the target tag.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="merge-target">Target Tag *</Label>
              <Select value={mergeTargetTagId} onValueChange={setMergeTargetTagId}>
                <SelectTrigger id="merge-target">
                  <SelectValue placeholder="Select target tag" />
                </SelectTrigger>
                <SelectContent>
                  {activeTagsForMerge.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      {tag.name} ({tag.assignment_count || 0} assignments)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setMergeSourceTag(null);
              setMergeTargetTagId('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleMergeTag}
              disabled={!mergeTargetTagId || mergeTag.isPending}
            >
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

